import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CpuChipIcon,
  ShieldCheckIcon,
  BoltIcon,
  CloudIcon,
  CircleStackIcon,
  CubeTransparentIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import {
  FaReact,
  FaNodeJs,
  FaDocker,
  FaAws
} from 'react-icons/fa';
import {
  SiTypescript,
  SiMongodb,
  SiPostgresql,
  SiRedis,
  SiGraphql,
  SiFirebase,
  SiNextdotjs,
  SiTailwindcss,
  SiPrisma,
  SiSocketdotio,
  SiStripe,
  SiAuth0
} from 'react-icons/si';

export default function WebAppDevelopment() {
  const { t } = useTranslation('webAppDev');
  const [isVisible, setIsVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const featuresRef = useRef(null);
  const processRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === featuresRef.current && entry.isIntersecting) {
            setFeaturesVisible(true);
          }
          if (entry.target === processRef.current && entry.isIntersecting) {
            setProcessVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (featuresRef.current) observer.observe(featuresRef.current);
    if (processRef.current) observer.observe(processRef.current);

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      Icon: CubeTransparentIcon,
      title: t('features.items.scalable.title'),
      description: t('features.items.scalable.description')
    },
    {
      Icon: ShieldCheckIcon,
      title: t('features.items.security.title'),
      description: t('features.items.security.description')
    },
    {
      Icon: BoltIcon,
      title: t('features.items.realtime.title'),
      description: t('features.items.realtime.description')
    },
    {
      Icon: CircleStackIcon,
      title: t('features.items.database.title'),
      description: t('features.items.database.description')
    },
    {
      Icon: CloudIcon,
      title: t('features.items.cloud.title'),
      description: t('features.items.cloud.description')
    },
    {
      Icon: ChartBarIcon,
      title: t('features.items.analytics.title'),
      description: t('features.items.analytics.description')
    }
  ];

  const process = [
    {
      step: "01",
      title: t('process.steps.discovery.title'),
      description: t('process.steps.discovery.description')
    },
    {
      step: "02",
      title: t('process.steps.architecture.title'),
      description: t('process.steps.architecture.description')
    },
    {
      step: "03",
      title: t('process.steps.development.title'),
      description: t('process.steps.development.description')
    },
    {
      step: "04",
      title: t('process.steps.testing.title'),
      description: t('process.steps.testing.description')
    },
    {
      step: "05",
      title: t('process.steps.deployment.title'),
      description: t('process.steps.deployment.description')
    }
  ];

  const technologies = [
    { name: "React", Icon: FaReact, color: "#61DAFB" },
    { name: "Next.js", Icon: SiNextdotjs, color: "#ffffff" },
    { name: "TypeScript", Icon: SiTypescript, color: "#3178C6" },
    { name: "Node.js", Icon: FaNodeJs, color: "#339933" },
    { name: "GraphQL", Icon: SiGraphql, color: "#E10098" },
    { name: "PostgreSQL", Icon: SiPostgresql, color: "#4169E1" },
    { name: "MongoDB", Icon: SiMongodb, color: "#47A248" },
    { name: "Redis", Icon: SiRedis, color: "#DC382D" },
    { name: "Firebase", Icon: SiFirebase, color: "#FFCA28" },
    { name: "Docker", Icon: FaDocker, color: "#2496ED" },
    { name: "AWS", Icon: FaAws, color: "#FF9900" },
    { name: "Prisma", Icon: SiPrisma, color: "#2D3748" },
    { name: "Socket.io", Icon: SiSocketdotio, color: "#010101" },
    { name: "Tailwind", Icon: SiTailwindcss, color: "#06B6D4" },
    { name: "Stripe", Icon: SiStripe, color: "#635BFF" },
    { name: "Auth0", Icon: SiAuth0, color: "#EB5424" },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative min-h-[70vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
          <div className={`max-w-3xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
              <Link to="/" className="hover:text-white transition-colors">{t('hero.breadcrumb.home')}</Link>
              <span>/</span>
              <span className="text-purple-400">{t('hero.breadcrumb.current')}</span>
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 backdrop-blur-sm rounded-full border border-purple-500/20 mb-6">
              <CpuChipIcon className="w-5 h-5 text-purple-400" />
              <span className="text-sm text-purple-300">{t('hero.badge')}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-6">
              {t('hero.title')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                {t('hero.titleHighlight')}
              </span>
            </h1>

            <p className="text-xl text-gray-400 leading-relaxed mb-10">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/Contact"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105"
              >
                {t('hero.cta')}
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Bento Grid */}
      <div ref={featuresRef} className="py-24 px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4">
              {t('features.badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('features.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500">{t('features.titleHighlight')}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('features.description')}
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[180px]">
            {/* Large tile - spans 2 cols */}
            {(() => {
              const Icon0 = features[0].Icon;
              return (
                <div
                  className="group col-span-2 row-span-1 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl p-8 flex flex-col justify-between hover:shadow-xl hover:shadow-purple-500/20 transition-all duration-300"
                  style={{
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease-out',
                  }}
                >
                  <Icon0 className="w-10 h-10 text-white" />
                  <div>
                    <h3 className="text-xl font-bold text-white">{features[0].title}</h3>
                    <p className="text-purple-100 mt-1 text-sm">{features[0].description}</p>
                  </div>
                </div>
              );
            })()}

            {/* Small tile */}
            {(() => {
              const Icon1 = features[1].Icon;
              return (
                <div
                  className="group bg-white border border-gray-200 rounded-3xl p-6 flex flex-col justify-between hover:border-purple-300 hover:shadow-lg transition-all duration-300"
                  style={{
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease-out',
                    transitionDelay: '100ms',
                  }}
                >
                  <Icon1 className="w-8 h-8 text-purple-500" />
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{features[1].title}</h3>
                    <p className="text-gray-500 text-xs mt-1">{features[1].description}</p>
                  </div>
                </div>
              );
            })()}

            {/* Small tile */}
            {(() => {
              const Icon2 = features[2].Icon;
              return (
                <div
                  className="group bg-gray-900 rounded-3xl p-6 flex flex-col justify-between hover:shadow-lg transition-all duration-300"
                  style={{
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease-out',
                    transitionDelay: '150ms',
                  }}
                >
                  <Icon2 className="w-8 h-8 text-pink-400" />
                  <div>
                    <h3 className="text-base font-bold text-white">{features[2].title}</h3>
                    <p className="text-gray-400 text-xs mt-1">{features[2].description}</p>
                  </div>
                </div>
              );
            })()}

            {/* Small tile */}
            {(() => {
              const Icon3 = features[3].Icon;
              return (
                <div
                  className="group bg-white border border-gray-200 rounded-3xl p-6 flex flex-col justify-between hover:border-purple-300 hover:shadow-lg transition-all duration-300"
                  style={{
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease-out',
                    transitionDelay: '200ms',
                  }}
                >
                  <Icon3 className="w-8 h-8 text-purple-500" />
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{features[3].title}</h3>
                    <p className="text-gray-500 text-xs mt-1">{features[3].description}</p>
                  </div>
                </div>
              );
            })()}

            {/* Small tile - moved for mobile layout */}
            {(() => {
              const Icon5 = features[5].Icon;
              return (
                <div
                  className="group bg-gray-900 rounded-3xl p-6 flex flex-col justify-between hover:shadow-lg transition-all duration-300 lg:order-last"
                  style={{
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease-out',
                    transitionDelay: '250ms',
                  }}
                >
                  <Icon5 className="w-8 h-8 text-purple-400" />
                  <div>
                    <h3 className="text-base font-bold text-white">{features[5].title}</h3>
                    <p className="text-gray-400 text-xs mt-1">{features[5].description}</p>
                  </div>
                </div>
              );
            })()}

            {/* Large tile - spans 2 cols */}
            {(() => {
              const Icon4 = features[4].Icon;
              return (
                <div
                  className="group col-span-2 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl p-8 flex items-center gap-6 hover:shadow-xl hover:shadow-pink-500/20 transition-all duration-300"
                  style={{
                    opacity: featuresVisible ? 1 : 0,
                    transform: featuresVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.5s ease-out',
                    transitionDelay: '300ms',
                  }}
                >
                  <Icon4 className="w-12 h-12 text-white flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-white">{features[4].title}</h3>
                    <p className="text-pink-100 mt-1 text-sm">{features[4].description}</p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* What We Build Section */}
      <div className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4">
              {t('solutions.badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('solutions.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('solutions.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: t('solutions.items.saas.title'), desc: t('solutions.items.saas.description') },
              { title: t('solutions.items.dashboards.title'), desc: t('solutions.items.dashboards.description') },
              { title: t('solutions.items.ecommerce.title'), desc: t('solutions.items.ecommerce.description') },
              { title: t('solutions.items.crm.title'), desc: t('solutions.items.crm.description') },
              { title: t('solutions.items.booking.title'), desc: t('solutions.items.booking.description') },
              { title: t('solutions.items.api.title'), desc: t('solutions.items.api.description') },
            ].map((item, index) => (
              <div
                key={index}
                className="group p-6 bg-gray-50 rounded-2xl hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                  <ArrowPathIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div ref={processRef} className="py-24 px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${processVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-4 py-2 bg-purple-500/10 text-purple-400 rounded-full text-sm font-semibold mb-4">
              {t('process.badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('process.title')}
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t('process.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="relative"
                style={{
                  transitionDelay: `${index * 150}ms`,
                  opacity: processVisible ? 1 : 0,
                  transform: processVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.7s ease-out',
                }}
              >
                <div className="text-5xl font-bold text-purple-500/20 mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {item.description}
                </p>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-6 right-0 translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-purple-500 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Technologies Section - Marquee Style */}
      <div className="py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4">
              {t('tech.badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('tech.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('tech.description')}
            </p>
          </div>
        </div>

        {/* Marquee Container */}
        <div className="relative">
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

          {/* Row 1 - Scroll Left */}
          <div className="mb-6 overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_10%,white_90%,transparent)]">
            <div className="flex gap-6 w-max animate-marquee hover:[animation-play-state:paused]">
              {[...technologies.slice(0, 8), ...technologies.slice(0, 8)].map((tech, index) => (
                <div
                  key={index}
                  className="group flex-shrink-0 flex items-center gap-4 px-6 py-4 bg-white rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-lg transition-all duration-300 cursor-default"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${tech.color}15` }}
                  >
                    <tech.Icon
                      className="w-7 h-7"
                      style={{ color: tech.color }}
                    />
                  </div>
                  <span className="text-base font-semibold text-gray-800 whitespace-nowrap">
                    {tech.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Row 2 - Scroll Right */}
          <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_10%,white_90%,transparent)]">
            <div className="flex gap-6 w-max animate-marquee-reverse hover:[animation-play-state:paused]">
              {[...technologies.slice(8, 16), ...technologies.slice(8, 16)].map((tech, index) => (
                <div
                  key={index}
                  className="group flex-shrink-0 flex items-center gap-4 px-6 py-4 bg-white rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-lg transition-all duration-300 cursor-default"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${tech.color}15` }}
                  >
                    <tech.Icon
                      className="w-7 h-7"
                      style={{ color: tech.color }}
                    />
                  </div>
                  <span className="text-base font-semibold text-gray-800 whitespace-nowrap">
                    {tech.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 px-6 lg:px-8 bg-gradient-to-br from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-purple-100 mb-10">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/Contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-purple-600 font-semibold rounded-full hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              {t('cta.button')}
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-8 text-purple-100">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5" />
              <span>{t('cta.features.discovery')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5" />
              <span>{t('cta.features.agile')}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5" />
              <span>{t('cta.features.team')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
